import React, { useEffect, useMemo, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../Translations/LenguageContext";
import SourcesModal from "./Sources/SourcesModal";
import ContextLoader from "../Loaders/ContextLoader";
import { PiMagnifyingGlass } from "react-icons/pi";
import { TbReload } from "react-icons/tb";
import { GrAscend, GrDescend } from "react-icons/gr";
import { HiOutlineDotsVertical } from "react-icons/hi";
import OpenContextModal from "./OpenContextModal";
import DeleteItemModal from "../Components/DeleteItemModal";
import OpenContextLogsModal from "./OpenContextLogsModal";
import OverviewSourceModal from "./OverviewSourceModal";
import SettingsContextModal from "./SettingsContextModal";
import OpenCustom from "./Sources/001OpenCustom";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const OpenContext = ({
    account_id,
    userData,
    singleOrgData,
    loadingSingleOrg,
    fetchSingleOrganization
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [openModal, setOpenModal] = useState(false);
    const { org_id, context_id } = useParams();
    const [loading, setLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState("asc");
    const [searchTerm, setSearchTerm] = useState("");
    const [data, setData] = useState(null);
    const [scanningWordIndex, setScanningWordIndex] = useState(0);
    const [isScanningWordsActive, setIsScanningWordsActive] = useState(false);
    const [openModalSource, setOpenModalSource] = useState(false);
    const [selectedSource, setSelectedSource] = useState(null)
    const [openDeleteItem, setOpenDeleteItem] = useState(false)
    const [openLogs, setOpenLogs] = useState(false)
    const [openTestModal, setOpenTestModal] = useState(false)
    const [openSettingsModal, setOpenSettingsModal] = useState(false)
    const [openDeleteContext, setOpenDeleteContext] = useState(false)
    const [openCustomModal, setOpenCustomModal] = useState(false)

    const [contextData, setContextData] = useState({
        id: "",
        name: "",
        description: "",
    });

    const scanningWords = [
        t.context.initializing,
        t.context.processing,
        t.context.extracting,
        t.context.indexing,
        t.context.building,
        t.context.almost_done
    ];

    const filteredAndSortedData = useMemo(() => {
        if (!Array.isArray(data?.sources)) return [];

        const term = searchTerm.toLowerCase().trim();

        const result = data.sources.filter((source) => {
            const searchableFields = [
                source.id,
                source.account_id,
                source.organization_id,
                source.context_id,
                source.source_type,
                JSON.stringify(source.source_urls ?? []),
                source.status,
                source.created_at,
                source.updated_at,
            ];

            return searchableFields.some((field) =>
                String(field ?? "").toLowerCase().includes(term)
            );
        });

        result.sort((a, b) => {
            const dateA = new Date(a.created_at).getTime() || 0;
            const dateB = new Date(b.created_at).getTime() || 0;

            return sortOrder === "asc"
                ? dateA - dateB
                : dateB - dateA;
        });

        return result;
    }, [data, searchTerm, sortOrder]);

    const fetchData = async () => {
        setScanningWordIndex(0);
        setIsScanningWordsActive(true);
        setLoading(true);

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_context_sources`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        context_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setData(
                result.data
                    ? {
                        ...result.data,
                        sources: [...(result.data.sources || [])].sort(
                            (a, b) =>
                                new Date(b.created_at).getTime() -
                                new Date(a.created_at).getTime()
                        ),
                    }
                    : null
            );

            setContextData({
                id: result.data.id,
                name: result.data.name,
                description: result.data.description
            })

        } catch (err) {
            console.error("Error fetching context sources:", err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    };

    const pollData = async () => {
        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_context_sources`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        context_id,
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            const updatedData = result.data
                ? {
                    ...result.data,
                    sources: [...(result.data.sources || [])].sort(
                        (a, b) =>
                            new Date(b.created_at).getTime() -
                            new Date(a.created_at).getTime()
                    ),
                }
                : null;

            setData(updatedData);

            setContextData({
                id: result.data.id,
                name: result.data.name,
                description: result.data.description
            })

            setSelectedSource((prev) => {
                if (!prev || !updatedData?.sources) {
                    return prev;
                }

                const updatedSource = updatedData.sources.find(
                    (source) => source.id === prev.id
                );

                return updatedSource ?? prev;
            });

        } catch (err) {
            console.error("Error polling context sources:", err);
        }
    };

    const exitContext = () => {
        navigate(`/console/organizations/${org_id}/context`)
    }

    useEffect(() => {
        if (!isScanningWordsActive) return;

        if (scanningWordIndex >= scanningWords.length - 1) {
            setIsScanningWordsActive(false);
            return;
        }

        const timeout = setTimeout(() => {
            setScanningWordIndex((prevIndex) =>
                Math.min(prevIndex + 1, scanningWords.length - 1)
            );
        }, 4000);

        return () => clearTimeout(timeout);
    }, [isScanningWordsActive, scanningWordIndex]);

    useEffect(() => {
        if (account_id && org_id && context_id) {
            fetchData();
        }
    }, [account_id, org_id, context_id]);

    useEffect(() => {
        const hasRunningSources = data?.sources?.some(
            (source) =>
                source.status === "QUEUED" ||
                source.status === "RUNNING"
        );

        if (!hasRunningSources) return;

        const interval = setInterval(() => {
            pollData();
        }, 5000);

        return () => clearInterval(interval);
    }, [data?.sources, account_id, org_id, context_id]);

    return (
        <div className="open_frame_cont">
            <div className="open_frame_cont_header">

                {loading ? (
                    <div className="open_frame_title_loading_cont">
                        <div className="open_frame_title_loading"></div>
                        <div className="open_frame_button_loading"></div>
                    </div>
                ) : (
                    <>
                        <div className="open_frame_title fade-in">
                            <button className="open_frame_cont_header_btn_exit" onClick={exitContext}><i className="fa-solid fa-chevron-left"></i></button>
                            {data?.name || context_id}
                        </div>
                        <button className="open_frame_cont_header_btn fade-in" onClick={() => setOpenSettingsModal(true)}>
                            <HiOutlineDotsVertical />
                        </button>
                    </>
                )}
            </div>

            {loading ? (
                <ContextLoader />
            ) : (
                <div className="open_frame_body">
                    {data?.sources?.length > 0 ? (
                        <div className="main_context_cont">
                            <div className="context_overview">
                                <div className="context_overview_inner">
                                    <p className="context_overview_title fade-in">
                                        {data?.name || ""}
                                    </p>

                                    <p className="context_overview_description fade-in">
                                        {data?.description || ""}
                                    </p>

                                    <button
                                        className="mainframe_item_create_btn fade-in"
                                        onClick={() => setOpenModal(true)}
                                    >
                                        <BiPlus /> {t.create}
                                    </button>
                                </div>
                                <div className="context_overview_inner_description_cont">
                                    <p className="context_overview_inner_description_cont_title fade-in">{t.context.open_context_title}</p>
                                    <p className="context_overview_inner_description_cont_description fade-in">{t.context.open_context_description}</p>
                                </div>
                            </div>

                            <div className="filtering_mainframe">
                                <div className="filtering_mainframe_btn_cont fade-in">
                                    {data?.sources?.length > 1 && (
                                        <>
                                            <button
                                                className={`filter_btn ${sortOrder === "desc" ? "active" : ""}`}
                                                onClick={() => setSortOrder("desc")} title="A-Z"
                                            >
                                                <GrAscend />
                                            </button>
                                            <button
                                                className={`filter_btn ${sortOrder === "asc" ? "active" : ""}`}
                                                onClick={() => setSortOrder("asc")}
                                                title="Z-A"
                                            >
                                                <GrDescend />
                                            </button>
                                        </>
                                    )}
                                    <button
                                        className="filter_btn"
                                        onClick={() => fetchData(org_id)}
                                        title={t.reload}
                                    >
                                        <TbReload />
                                    </button>
                                </div>
                                <div className="filter_input_cont fade-in">
                                    <input
                                        className="filter_input"
                                        placeholder={t.search_filter}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <span className="filter_input_icon"><PiMagnifyingGlass /></span>
                                </div>
                            </div>

                            {filteredAndSortedData.length > 0 ? (
                                <div className="context_window_cont">
                                    {filteredAndSortedData.map((source) => (
                                        <div
                                            className={`context_window fade-in ${source.status === "COMPLETED"
                                                ? "context_window_completed"
                                                : source.status === "QUEUED"
                                                    ? "context_window_queued"
                                                    : source.status === "FAILED"
                                                        ? "context_window_failed"
                                                        : "context_window_running"
                                                }`}
                                            key={source.id}
                                        >
                                            <div className="context_window_header">
                                                <div className="custom_cell_status">
                                                    {source.status == "COMPLETED" ? (
                                                        <span className="status_dot active"></span>
                                                    ) : source.status == "FAILED" ? (
                                                        <span className="status_dot error"></span>
                                                    ) : (
                                                        <span className="status_dot uploading"></span>
                                                    )}
                                                </div>
                                            </div>
                                            {(source.status == "QUEUED" || source.status == "RUNNING") ? (
                                                <div className="context_window_body">
                                                    <div className="context_source_type_loading" key={scanningWordIndex}>
                                                        <p className="fade-in">{scanningWords[scanningWordIndex]}</p>
                                                    </div>
                                                    <div className="scanning_animation_cont">
                                                        <div className="scanner_animation"></div>
                                                        <div className="scanner_animation"></div>
                                                        <div className="scanner_animation"></div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="context_window_body">
                                                    <div className="context_source_type">
                                                        {source.source_type}
                                                    </div>
                                                    <div className="context_sources">
                                                        {source.source_type == "website" ? (
                                                            <>
                                                                {source.source_urls.length > 1 ? (
                                                                    <p className="context_sources_p_source" title={source.source_urls}>
                                                                        {source.source_urls.length} {t.pages}
                                                                    </p>
                                                                ) : (
                                                                    <p className="context_sources_p_source" title={source.source_urls}>
                                                                        {source.source_urls}
                                                                    </p>
                                                                )}

                                                            </>
                                                        ) : (
                                                            <>
                                                                {source.source_urls.length > 1 ? (
                                                                    <p className="context_sources_p_source" title={source.source_urls}>
                                                                        {source.source_urls.length} {t.documents}
                                                                    </p>
                                                                ) : (
                                                                    <p className="context_sources_p_source" title={source.source_urls}>
                                                                        {source.source_urls.length} {t.document}
                                                                    </p>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="context_window_footer">
                                                <button
                                                    className="context_window_footer_btn"
                                                    onClick={() => { setOpenModalSource(true); setSelectedSource(source) }}
                                                >
                                                    <i><HiOutlineDotsVertical /></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="organizations_cont_no_items_mex fade-in">
                                    <p>{t.no_data_found_by_search}</p>
                                </div>
                            )}

                        </div>
                    ) : (
                        <div className="add_new_source_cont">
                            <p className="add_new_source_cont_title fade-in">{t.context.add_new_source_title}</p>
                            <p className="add_new_source_cont_desc fade-in">{t.context.add_new_source_description}</p>
                            <button
                                className="mainframe_item_create_btn mainframe_item_create_btn_anim fade-in"
                                onClick={() => setOpenModal(true)}
                            >
                                <BiPlus /> {t.create}
                            </button>

                        </div>
                    )}
                </div>
            )}

            <SourcesModal
                account_id={account_id}
                show={openModal}
                handleClose={() => setOpenModal(false)}
                fetchRecords={fetchData}
                userData={userData}
            />

            <OpenContextModal
                show={openModalSource}
                handleClose={() => setOpenModalSource(false)}
                selectedSource={selectedSource}
                setOpenDeleteItem={setOpenDeleteItem}
                setOpenLogs={setOpenLogs}
                setOpenTestModal={setOpenTestModal}
            />

            <OpenContextLogsModal
                account_id={account_id}
                show={openLogs}
                handleClose={() => setOpenLogs(false)}
                selectedSource={selectedSource?.id}
                openContextModal={() => setOpenModalSource(true)}
            />

            <OverviewSourceModal
                account_id={account_id}
                context_id={context_id}
                org_id={org_id}
                selectedSource={selectedSource}
                show={openTestModal}
                handleClose={() => setOpenTestModal(false)}
                openContextModal={() => setOpenModalSource(true)}
                setOpenCustomModal={setOpenCustomModal}
            />

            <OpenCustom
                account_id={account_id}
                context_id={context_id}
                org_id={org_id}
                selectedSource={selectedSource}
                show={openCustomModal}
                handleClose={() => setOpenCustomModal(false)}
                setOpenTestModal={setOpenTestModal}
            />

            <SettingsContextModal
                account_id={account_id}
                show={openSettingsModal}
                handleClose={() => setOpenSettingsModal(false)}
                fetchRecords={fetchData}
                setContextData={setContextData}
                contextData={contextData}
                setOpenDeleteContext={setOpenDeleteContext}
            />

            <DeleteItemModal
                account_id={account_id}
                show={openDeleteItem}
                handleClose={() => setOpenDeleteItem(false)}
                fetchRecords={fetchData}
                item={selectedSource}
                endpoint={"delete_context_source"}
                title={t.context.delete_title_source}
                description={t.context.delete_desc_source}
                input_value={selectedSource?.source_type}
            />
            <DeleteItemModal
                account_id={account_id}
                show={openDeleteContext}
                handleClose={() => setOpenDeleteContext(false)}
                fetchRecords={exitContext}
                item={contextData}
                endpoint={"delete_context"}
                title={t.context.delete_title}
                description={t.context.delete_desc}
                input_value={contextData}
            />

        </div>
    );
};

export default OpenContext;